import type { ConstantsInvoiceStruct } from "@gosaas/types";
import {
	IconExternalLink,
	IconFileDownload,
	IconReceipt,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { reportQueryError } from "@/utils/query-errors";

/** Formats a Stripe amount (smallest currency unit) for display. */
export function formatInvoiceAmount(amount: number, currency: string): string {
	const code = (currency || "usd").toUpperCase();
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: code,
		}).format(amount / 100);
	} catch {
		return `${(amount / 100).toFixed(2)} ${code}`;
	}
}

export function invoiceStatusVariant(
	status: string,
): "default" | "secondary" | "destructive" | "outline" {
	switch (status) {
		case "paid":
			return "default";
		case "open":
			return "secondary";
		case "uncollectible":
		case "void":
			return "destructive";
		default:
			return "outline";
	}
}

export function formatInvoiceDate(value?: string): string {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

export const useFetchInvoices = (
	teamId: string | null,
	options?: { enabled?: boolean },
) => {
	const enabled = options?.enabled ?? !!teamId;
	const {
		data: invoices,
		isLoading: isLoadingInvoices,
		refetch: refetchInvoices,
	} = useQuery({
		queryKey: ["billing-invoices", teamId],
		queryFn: async () => {
			try {
				const response = await requestWithHandling<ConstantsInvoiceStruct[]>(
					() => apiClient.api.billingInvoicesList(teamId ?? ""),
				);
				if (response.code === "SUCCESS") {
					return response.data ?? [];
				}
				throw new Error(response.message || "Failed to fetch invoices");
			} catch (err) {
				reportQueryError(err, {
					title: "Failed to load invoices",
					silent: true,
				});
			}
		},
		enabled: enabled && !!teamId,
		staleTime: 30_000,
		retry: (failureCount, error) => {
			if (
				error instanceof TypedClientApiError &&
				error.code === "RATE_LIMIT_EXCEEDED"
			) {
				return false;
			}
			return failureCount < 1;
		},
		retryDelay: 1000,
	});

	return { invoices, isLoadingInvoices, refetchInvoices };
};

export function shouldShowInvoiceHistory(
	isSubscribed: boolean,
	isLoading: boolean,
	invoiceCount: number,
): boolean {
	if (!isSubscribed) {
		return false;
	}
	if (isLoading) {
		return true;
	}
	return invoiceCount > 0;
}

export function InvoiceHistoryCard({
	teamId,
	isSubscribed = false,
}: {
	teamId: string | null;
	isSubscribed?: boolean;
}) {
	const { invoices, isLoadingInvoices } = useFetchInvoices(teamId, {
		enabled: isSubscribed,
	});

	if (
		!shouldShowInvoiceHistory(
			isSubscribed,
			isLoadingInvoices,
			(invoices ?? []).length,
		)
	) {
		return null;
	}

	return (
		<Card className="mt-8 rounded-lg border shadow-sm">
			<CardHeader className="pb-4">
				<CardTitle className="flex items-center gap-2">
					<IconReceipt className="size-5" aria-hidden />
					Invoice History
				</CardTitle>
				<CardDescription>
					Your billing history. Open an invoice for the full receipt or download
					the PDF.
				</CardDescription>
			</CardHeader>
			<CardContent className="pt-0">
				{isLoadingInvoices && (
					<div className="space-y-2">
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
						<Skeleton className="h-10 w-full" />
					</div>
				)}
				{!isLoadingInvoices && (invoices ?? []).length > 0 && (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Invoice</TableHead>
								<TableHead>Date</TableHead>
								<TableHead>Status</TableHead>
								<TableHead className="text-right">Amount</TableHead>
								<TableHead className="text-right">Links</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{(invoices ?? []).map((invoice) => (
								<TableRow key={invoice.id}>
									<TableCell className="font-medium">
										{invoice.number || "—"}
									</TableCell>
									<TableCell>{formatInvoiceDate(invoice.issued_at)}</TableCell>
									<TableCell>
										<Badge variant={invoiceStatusVariant(invoice.status ?? "")}>
											{invoice.status}
										</Badge>
									</TableCell>
									<TableCell className="text-right">
										{formatInvoiceAmount(
											invoice.amount_due ?? 0,
											invoice.currency ?? "usd",
										)}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex items-center justify-end gap-1">
											{invoice.hosted_invoice_url && (
												<Button
													variant="ghost"
													size="icon"
													className="size-7"
													aria-label="Open invoice"
													onClick={() =>
														window.open(
															invoice.hosted_invoice_url,
															"_blank",
															"noopener,noreferrer",
														)
													}
												>
													<IconExternalLink className="size-4" aria-hidden />
												</Button>
											)}
											{invoice.invoice_pdf && (
												<Button
													variant="ghost"
													size="icon"
													className="size-7"
													aria-label="Download invoice PDF"
													onClick={() =>
														window.open(
															invoice.invoice_pdf,
															"_blank",
															"noopener,noreferrer",
														)
													}
												>
													<IconFileDownload className="size-4" aria-hidden />
												</Button>
											)}
										</div>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
}
